<script setup>
import { computed } from 'vue';
import { format, fromUnixTime, isSameDay, isSameYear } from 'date-fns';

import MessageStatus from './MessageStatus.vue';
import Icon from 'next/icon/Icon.vue';
import { useInbox } from 'dashboard/composables/useInbox';
import { useMessageContext } from './provider.js';

import { MESSAGE_STATUS, MESSAGE_TYPES } from './constants';

const {
  isAFacebookInbox,
  isALineChannel,
  isAPIInbox,
  isASmsInbox,
  isATelegramChannel,
  isATwilioChannel,
  isAWebWidgetInbox,
  isAWhatsAppChannel,
  isAnEmailChannel,
  isAnInstagramChannel,
  isATiktokChannel,
  isAZernioChannel,
} = useInbox();

const {
  status,
  isPrivate,
  createdAt,
  sourceId,
  messageType,
  contentAttributes,
} = useMessageContext();

// [metrica] Upstream rendered 'LLL d, h:mm a' — "Jul 21, 6:42 PM": an English
// month name and a 12-hour clock, in a pt-BR product (date-fns runs on its
// en-US default here; nothing in app/javascript ever calls setDefaultOptions or
// imports a locale). It was also wide enough that on a short message the meta
// row, not the text, decided the bubble's width. These formats carry no month
// NAME, so they are locale-independent by construction, and they escalate only
// as far as the age of the message requires.
const readableTime = computed(() => {
  const messageTime = fromUnixTime(createdAt.value);
  const now = new Date();
  if (isSameDay(messageTime, now)) return format(messageTime, 'HH:mm');
  if (isSameYear(messageTime, now)) return format(messageTime, 'dd/MM HH:mm');
  return format(messageTime, 'dd/MM/yyyy HH:mm');
});

// Full absolute timestamp for the <time> element's tooltip — the compact label
// above deliberately drops information, so keep it reachable on hover.
const absoluteTime = computed(() =>
  format(fromUnixTime(createdAt.value), 'dd/MM/yyyy HH:mm')
);

const showStatusIndicator = computed(() => {
  if (isPrivate.value) return false;
  // Don't show status for failed messages, we already show error message
  if (status.value === MESSAGE_STATUS.FAILED) return false;
  // Don't show status for deleted messages
  if (contentAttributes.value?.deleted) return false;

  if (messageType.value === MESSAGE_TYPES.OUTGOING) return true;
  if (messageType.value === MESSAGE_TYPES.TEMPLATE) return true;

  return false;
});

const isSent = computed(() => {
  if (!showStatusIndicator.value) return false;

  // Zernio (Instagram DM / Messenger via the partner) is a custom channel, so it
  // isn't in the lists below — without this it always fell through to PROGRESS (the
  // pending clock). Its provider message id isn't reliably matchable, so drive the
  // ticks off status alone (a failed send is already excluded above).
  if (isAZernioChannel.value) return status.value === MESSAGE_STATUS.SENT;

  // Messages will be marked as sent for the Email channel if they have a source ID.
  if (isAnEmailChannel.value) return !!sourceId.value;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isAFacebookInbox.value ||
    isASmsInbox.value ||
    isATelegramChannel.value ||
    isAnInstagramChannel.value ||
    isATiktokChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.SENT;
  }

  // API inbox messages use real sent/delivered/read status values from the external system.
  if (isAPIInbox.value) return status.value === MESSAGE_STATUS.SENT;

  // All messages will be mark as sent for the Line channel, as there is no source ID.
  if (isALineChannel.value) return true;

  return false;
});

const isDelivered = computed(() => {
  if (!showStatusIndicator.value) return false;

  if (isAZernioChannel.value) return status.value === MESSAGE_STATUS.DELIVERED;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isASmsInbox.value ||
    isAFacebookInbox.value ||
    isAnInstagramChannel.value ||
    isATiktokChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.DELIVERED;
  }
  // API inbox messages use real delivered status from the external system.
  if (isAPIInbox.value) return status.value === MESSAGE_STATUS.DELIVERED;
  // All messages marked as delivered for the web widget inbox once they are sent.
  if (isAWebWidgetInbox.value) {
    return status.value === MESSAGE_STATUS.SENT;
  }
  if (isALineChannel.value) {
    return status.value === MESSAGE_STATUS.DELIVERED;
  }

  return false;
});

const isRead = computed(() => {
  if (!showStatusIndicator.value) return false;

  if (isAZernioChannel.value) return status.value === MESSAGE_STATUS.READ;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isAFacebookInbox.value ||
    isAnInstagramChannel.value ||
    isATiktokChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.READ;
  }

  if (isAWebWidgetInbox.value || isAPIInbox.value) {
    return status.value === MESSAGE_STATUS.READ;
  }

  return false;
});

const statusToShow = computed(() => {
  if (isRead.value) return MESSAGE_STATUS.READ;
  if (isDelivered.value) return MESSAGE_STATUS.DELIVERED;
  if (isSent.value) return MESSAGE_STATUS.SENT;

  return MESSAGE_STATUS.PROGRESS;
});
</script>

<template>
  <div class="text-xs flex items-center gap-1.5 tabular-nums">
    <div class="inline">
      <time class="inline" :title="absoluteTime">{{ readableTime }}</time>
    </div>
    <Icon v-if="isPrivate" icon="i-lucide-lock-keyhole" class="size-3" />
    <MessageStatus v-if="showStatusIndicator" :status="statusToShow" />
  </div>
</template>
`
